import { z } from 'zod';
import type { IHistoryDemographicsModel } from '@/types/history/history-demographics';
import { requireFields, yesNo } from '@utils/form-schema';

const PHONE_ALLOWED_REGEX = /^[0-9()-]+$/;
const PHONE_MIN_DIGITS = 6;
const PHONE_MAX_LENGTH = 15;

type TSchemaFlags = Pick<IHistoryDemographicsModel, 'askForId' | 'isLwrAvailable' | 'wageReimbursementPtoValues'>;

export const createHistoryDemographicsSchema = (model: TSchemaFlags) => {
    return z
        .object({
            ethnicity: z.string(),

            identificationTab: z.string(),
            ssn: z.string(),
            dlIssueCountry: z.string(),
            dlIssueState: z.string(),
            dlNonUsStateProvince: z.string(),
            dlNumber: z.string(),
            usCountryValue: z.number(),
            mothersMaiden: z.string(),

            emergencyContact: z.string(),
            emergencyContactPhone: z.string(),
            emergencyContactRelationship: z.string(),

            isCurrentlyEmployed: yesNo,
            occupation: z.string(),
            occupationHasHeavyLifting: yesNo,
            medicalPto: z.string(),
            wageReimbursementImportance: z.string(),
            annualIncomeRange: z.string(),
            wantsWageReimbursement: yesNo,

            hasHealthInsurance: yesNo,
            hasPrimaryCarePhysician: yesNo,
            physicianName: z.string(),
        })
        .superRefine((data, ctx) => {
            requireFields(ctx, data, 'ethnicity');

            // HasRequiredId: SSN, or a complete Govt-ID group (number + state/province + mother's maiden).
            if (model.askForId) {
                if (data.identificationTab === 'ssn') {
                    requireFields(ctx, data, 'ssn');
                } else {
                    const isUsIssuer = data.dlIssueCountry === String(data.usCountryValue);

                    requireFields(ctx, data, 'dlIssueCountry');

                    if (isUsIssuer) {
                        requireFields(ctx, data, 'dlIssueState');
                    } else {
                        requireFields(ctx, data, 'dlNonUsStateProvince');
                    }

                    requireFields(ctx, data, 'dlNumber', 'mothersMaiden');
                }
            }

            requireFields(ctx, data, 'emergencyContact');

            if (!data.emergencyContactPhone) {
                ctx.addIssue({ code: 'custom', path: ['emergencyContactPhone'], message: 'Required' });
            } else if (
                !PHONE_ALLOWED_REGEX.test(data.emergencyContactPhone) ||
                data.emergencyContactPhone.length > PHONE_MAX_LENGTH ||
                (data.emergencyContactPhone.match(/\d/g) ?? []).length < PHONE_MIN_DIGITS
            ) {
                ctx.addIssue({ code: 'custom', path: ['emergencyContactPhone'], message: 'Can only contain numbers, hyphen and parentheses, and at least 6 numbers' });
            }

            requireFields(ctx, data, 'emergencyContactRelationship', 'isCurrentlyEmployed');

            if (data.isCurrentlyEmployed === '1') {
                requireFields(ctx, data, 'occupation', 'occupationHasHeavyLifting', 'medicalPto');

                if (model.isLwrAvailable) {
                    requireFields(ctx, data, 'wageReimbursementImportance', 'annualIncomeRange');

                    // AskWageReimbursementQuestion: PTO indicates the donor is not reimbursed.
                    const notReimbursed = model.wageReimbursementPtoValues
                        .map(String)
                        .includes(data.medicalPto);

                    if (notReimbursed) {
                        requireFields(ctx, data, 'wantsWageReimbursement');
                    }
                }
            }

            requireFields(ctx, data, 'hasHealthInsurance', 'hasPrimaryCarePhysician');
        });
};
